import { getErrorMessage } from '@utils/error-message';
import { stubTrue } from 'lodash';
import React, { useCallback, useContext, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Typography from '@material-ui/core/Typography';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import {
  AbortableFetch,
  ActionButtonHandle,
  DetailContext,
  DetailMode,
  EvidenceContext,
  FileRef,
  PagedTableFieldContext,
  SnackbarContext,
  TableFieldToolbarButton,
  abortableFetch,
  useEventCallback,
} from '@eas/common-web';
import { InternalDocumentFields } from '@modules/document-internal/fields/general/general';
import { useValidationSchema } from '@modules/document-internal/internal-document-schema';
import { DECISION_SOURCE } from '@modules/es-file/es-file-hooked-sources';
import { AttachmentProvider } from '@composite/attachments/attachment-context';
import { DetailActionButton } from '@components/action-button/detail-action-button';
import { Document, ErrorObject, InternalDocument } from '@models';
import { AttachmentType, DecisionForm, EvidenceApiUrl, UIElement } from '@enums';
import { AttachmentField } from '../../attachments/attachment-field';
import { DocumentDialogDataProvider } from '../document-context';

type DecisionCreateDto = {
  form: DecisionForm;
  decisionDate: string;
  attachments: {
    fullTextDocx: FileRef;
  };
};

export function useAddInternalDocumentDialog(
  setResult: (document: InternalDocument | null) => void,
  refreshSources: () => void
) {
  const fetch = useRef<AbortableFetch | null>(null);
  const { showSnackbar } = useContext(SnackbarContext);

  const decisionCreateDtoRef = useRef<DecisionCreateDto | null>(null);

  const callCreateApi = useEventCallback(
    (id: string, formData: InternalDocument) => {
      const decisionFile = formData.decision;

      if (decisionFile) {
        decisionCreateDtoRef.current = {
          form: formData.linkedDecision!.form!,
          decisionDate: formData.linkedDecision!.decisionDate!,
          attachments: {
            fullTextDocx: formData.linkedDecision!.attachments!.fullTextDocx!,
          },
        };
      }

      return abortableFetch(`${EvidenceApiUrl.DOCUMENTS}`, {
        method: 'POST',
        headers: new Headers({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify(formData),
      });
    }
  );

  const callEditApi = useEventCallback((formData: InternalDocument) => {
    return abortableFetch(`${EvidenceApiUrl.DOCUMENTS}/${formData.id}`, {
      method: 'PUT',
      headers: new Headers({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(formData),
    });
  });

  const callCreateDecisionApi = useEventCallback((id: string) => {
    return abortableFetch(`${EvidenceApiUrl.DOCUMENTS}/${id}/decision`, {
      method: 'POST',
      headers: new Headers({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(decisionCreateDtoRef.current),
    });
  });

  const handleEditDocument = useEventCallback(
    async (values: InternalDocument) => {
      try {
        if (fetch.current !== null) {
          fetch.current.abort();
        }

        fetch.current = callEditApi(values);
        const document: InternalDocument = await fetch.current.json();

        if (fetch.current !== null) {
          fetch.current.abort();
        }

        if (document.decision) {
          fetch.current = callCreateDecisionApi(document.id);
          await fetch.current.raw();
        }

        refreshSources();
        setResult(null);
        decisionCreateDtoRef.current = null;
      } catch (e) {
        const err = e as ErrorObject<string>;

        const message = getErrorMessage(err);

        if (err.name !== 'AbortError') {
          showSnackbar(...message);
        }
      }
    }
  );

  return {
    callCreateApi,
    handleEditDocument,
  };
}

export function AddInternalDocumentButton({
  initialValues,
  disabled,
  refreshAfterConfirm = false,
}: {
  initialValues: Document;
  disabled: boolean;
  refreshAfterConfirm?: boolean;
}) {
  const intl = useIntl();

  const { source: documentSource } = useContext(PagedTableFieldContext);
  const { hookedSources } = useContext(EvidenceContext);
  const { refreshAll } = useContext(DetailContext);
  const decisionsSource = hookedSources[DECISION_SOURCE]?.source;

  const [result, setResult] = useState<Document | null>(null);
  const resultRef = useRef<Document | null>(result);
  resultRef.current = result;
  const attachmentRef = useRef<ActionButtonHandle>(null);

  const validationSchema = useValidationSchema();

  const refreshSources = () => {
    documentSource.reset();
    documentSource.loadMore();

    decisionsSource.reset();
    decisionsSource.loadMore();
  };

  const { callCreateApi, handleEditDocument } = useAddInternalDocumentDialog(
    setResult,
    () => {
      if (refreshAfterConfirm) {
        refreshAll();
      }
      refreshSources();
    }
  );

  const AttachmentFormFields = useCallback(() => {
    const document = resultRef.current;

    if (!document?.id) {
      return null;
    }

    return (
      <DocumentDialogDataProvider document={document}>
        {({ source }) => (
          <>
            <InternalDocumentFields enableEditdecision={false} />
            <AttachmentProvider>
              <AttachmentField
                label={
                  <FormattedMessage
                    id="ES__DOCUMENT_OUTGOING__DETAIL__PANEL_TITLE__INTERNAL"
                    defaultMessage="Přílohy interního dokumentu"
                  />
                }
                documentId={document.id}
                defaultName={document.summary ?? ''}
                attachments={source.data?.attachments ?? []}
                attachmentType={AttachmentType.INTERNAL}
                defaultExpanded={true}
                hasPermission={stubTrue}
              />
            </AttachmentProvider>
          </>
        )}
      </DocumentDialogDataProvider>
    );
  }, []);

  return (
    <>
      <DetailActionButton
        promptKey="ADD_INTERNAL_DOCUMENT"
        apiCall={callCreateApi}
        buttonLabel={intl.formatMessage({
          id: 'ES__DOCUMENTS_TOOLBAR__ADD_INTERNAL_DOCUMENT',
          defaultMessage: 'Přidat interní dokument',
        })}
        dialogTitle={intl.formatMessage({
          id: 'ES__DOCUMENTS_TOOLBAR__ADD_INTERNAL_DOCUMENT__TITLE',
          defaultMessage: 'Interní dokument',
        })}
        dialogText={intl.formatMessage({
          id: 'ES__DOCUMENTS_TOOLBAR__ADD_INTERNAL_DOCUMENT__TEXT',
          defaultMessage: 'Vyplňte formulář.',
        })}
        dialogWidth={800}
        modes={[DetailMode.VIEW, DetailMode.EDIT]}
        ButtonComponent={function Button({ label, onClick }) {
          return (
            <TableFieldToolbarButton
              IconComponent={AddCircleOutlineIcon}
              show={true}
              disabled={disabled ?? false}
              title={label}
              onClick={onClick}
              color="primary"
              variant="contained"
            >
              <Typography variant="body2">
                {intl.formatMessage({
                  id: 'ES__DOCUMENTS_TOOLBAR__ADD_INTERNAL_DOCUMENT__BUTTON',
                  defaultMessage: 'Přidat interní',
                })}
              </Typography>
            </TableFieldToolbarButton>
          );
        }}
        FormFields={function Fields() {
          return (
            <DocumentDialogDataProvider document={result!}>
              {() => (
                <InternalDocumentFields
                  createPermission={(element) =>
                    element === UIElement.InternalDocument.SUMMARY ||
                    element === UIElement.InternalDocument.NOTE ||
                    element === UIElement.InternalDocument.PAGE_COUNT
                  }
                />
              )}
            </DocumentDialogDataProvider>
          );
        }}
        formInitialValues={initialValues}
        formValidationSchema={validationSchema}
        refreshAfterConfirm={false}
        onResult={async (result: Document) => {
          setResult(result);

          requestAnimationFrame(() => {
            attachmentRef.current?.executeAction();
          });
        }}
      />
      <DetailActionButton
        ref={attachmentRef}
        promptKey="ADD_INTERNAL_DOCUMENT_ATTACHMENTS"
        closeCallback={refreshSources}
        buttonLabel={intl.formatMessage({
          id: 'ES__DOCUMENTS_TOOLBAR__ADD_INTERNAL_DOCUMENT_ATTACHMENTS',
          defaultMessage: 'Přidat přílohy',
        })}
        dialogTitle={intl.formatMessage({
          id: 'ES__DOCUMENTS_TOOLBAR__ADD_INTERNAL_DOCUMENT_ATTACHMENTS__TITLE',
          defaultMessage: 'Přílohy interního dokumentu',
        })}
        dialogText={intl.formatMessage({
          id: 'ES__DOCUMENTS_TOOLBAR__ADD_INTERNAL_DOCUMENT_ATTACHMENTS__TEXT',
          defaultMessage: 'Vyplňte formulář.',
        })}
        dialogWidth={800}
        dialogShowClose={false}
        modes={[]}
        submitCallback={handleEditDocument}
        refreshAfterConfirm={false}
        formInitialValues={result}
        dialogDisableBackdrop={true}
        FormFields={AttachmentFormFields}
      />
    </>
  );
}
